import { useEffect, useMemo, useState } from 'react'
import { useFleet } from '../../state/store'
import {
  ago, duration, lastSeen, num, pct, STALE_AFTER_MS, STATE_COLOR, unit, vehicleState,
  type VehicleState,
} from '../../lib/format'
import { locate, type FenceIndex } from '../../lib/pointInPolygon'
import { loadLabel } from '../../map/layers/vehicles'
import {
  estimateRange, withCorridorForecast, MAX_PLAUSIBLE_KWH_PER_KM, MIN_LEG_KM, MIN_PLAUSIBLE_KWH_PER_KM,
} from '../../lib/range'
import { buildVehicleFlags, type VehicleFlag } from '../../lib/riskFlags'
import type { Breakdown, TimsTrip, Vehicle, VehicleMeta } from '../../lib/types'
import { overlayDriver, resolveTimsTrip, timsDetailFields } from '../../lib/timsTrip'
import { rewriteStopMessage } from '../../lib/stopPlace'
import type { DetailField, DetailSection } from './types'

export interface VehicleDetailModel {
  vehicle: Vehicle
  meta: VehicleMeta | undefined
  down: Breakdown | undefined
  state: VehicleState
  stateColor: string
  where: FenceIndex | null
  seenLabel: string
  load: string | null
  loadRoute: string | null
  flag: VehicleFlag | undefined
  sections: DetailSection[]
  driverPhone: string | null
  driverName: string | null
  trip: TimsTrip | undefined
}

/**
 * Join live store slices into the card/drawer/modal section model for one vehicle.
 */
export function useVehicleDetail(vehicleId: string | null): VehicleDetailModel | null {
  const vehicle = useFleet((s) => (vehicleId ? s.vehicles.get(vehicleId) : undefined))
  const kpi = useFleet((s) => (vehicleId ? s.kpis.get(vehicleId) : undefined))
  const fenceIndex = useFleet((s) => s.fenceIndex)
  const down = useFleet((s) => (vehicleId ? s.breakdowns.get(vehicleId) : undefined))
  const meta = useFleet((s) => (vehicleId ? s.meta.get(vehicleId) : undefined))
  const timsTrips = useFleet((s) => s.timsTrips)
  const fleetEfficiency = useFleet((s) => s.fleetEfficiency)
  const trip = useMemo(
    () => (vehicleId ? resolveTimsTrip(vehicleId, timsTrips) : undefined),
    [vehicleId, timsTrips],
  )
  const progress = useFleet((s) => (vehicleId ? s.progress.get(vehicleId) : undefined))
  const alerts = useFleet((s) => s.alerts)
  const geofences = useFleet((s) => s.geofences)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(id)
  }, [])

  return useMemo(() => {
    if (!vehicleId || !vehicle) return null

    const state = vehicleState(vehicle, now)
    const seen = lastSeen(vehicle)
    const where = vehicle.lat != null && vehicle.lon != null && fenceIndex.length
      ? locate(vehicle.lon, vehicle.lat, fenceIndex)
      : null
    const range = withCorridorForecast(
      vehicle,
      estimateRange(vehicle, meta, progress, now, trip, fleetEfficiency),
      progress,
    )
    const flag = buildVehicleFlags(
      [vehicle],
      alerts,
      new Map(progress ? [[vehicleId, progress]] : []),
      new Map(meta ? [[vehicleId, meta]] : []),
      now,
    ).get(vehicleId)

    const load = loadLabel(meta, trip, progress)
    const loadRoute = load
      ? (load === 'Loaded' ? 'Loaded · Dhar → Dhule' : 'Unloaded · Dhule → Dhar')
        + (meta?.route_id ? ` · ${meta.route_id}` : '')
      : null

    const sections: DetailSection[] = []

    const driverName = overlayDriver(trip, meta)
    if (trip || driverName) {
      const logisticsFields: DetailField[] = trip
        ? timsDetailFields(trip, { driverName })
        : [{ label: 'driver', value: driverName ?? '—' }]
      sections.push({
        id: 'logistics',
        title: 'Trip · TIMS',
        subtitle: trip?.event
          ? trip.event
          : (trip?.lr_number ? `LR ${trip.lr_number}` : 'Crew and lorry receipt'),
        highlights: logisticsFields.slice(0, 4),
        fields: logisticsFields,
        expandOnCard: true,
        wide: true,
        replayable: true,
      })
    }

    sections.push({
      id: 'tripreplay',
      title: 'Trip replay',
      subtitle: 'Geotags, halts and GPS path from Postgres',
      highlights: [
        { label: 'source', value: 'completed legs' },
        { label: 'map', value: 'teal GPS trail + scrubber' },
      ],
      fields: [
        { label: 'source', value: 'Postgres trip + trip_path' },
        { label: 'replay', value: 'latest completed leg for this plate' },
      ],
      replayable: true,
    })

    if (flag) {
      const lines = flag.alerts.slice(0, 5).map((a) => {
        const msg = rewriteStopMessage(a.message, vehicle.lat, vehicle.lon, geofences)
        return msg ? `${a.kind} — ${msg}` : a.kind
      })
      sections.push({
        id: 'flag',
        title: flag.label,
        subtitle: 'Live map flag',
        accent: flag.severity === 'critical' ? 'danger' : 'warn',
        highlights: [
          { label: 'severity', value: flag.severity },
          { label: 'kind', value: flag.kind },
          { label: 'alerts', value: String(flag.alerts.length) },
        ],
        fields: [
          { label: 'severity', value: flag.severity },
          { label: 'kind', value: flag.kind },
          { label: 'alerts', value: String(flag.alerts.length) },
        ],
        body: flag.detail,
        lines,
      })
    }

    const rangeFields: DetailField[] = [
      { label: 'usable kWh', value: unit(range.usable_kwh, 'kWh', 1) },
      {
        label: 'kWh/km',
        value: range.kwh_per_km == null ? '—'
          : `${range.kwh_per_km} (${range.kwh_per_km_source})`,
      },
      { label: 'km left', value: unit(range.km_remaining, 'km', 1) },
      ...(progress?.forecast_confidence != null || progress?.eta_min != null
        ? [{
            label: 'ETA',
            value: progress.eta_min != null ? `${progress.eta_min} min` : '—',
          }]
        : []),
      { label: 'range left', value: unit(range.est_range_km, 'km', 1) },
      {
        label: 'exp. SoC',
        // Floored at 0 -- a pack cannot hold less than nothing. When the arithmetic runs past
        // empty the overshoot is shown as a distance in the row below, where it means
        // something a dispatcher can act on.
        value: range.expected_soc_pct == null ? '—' : `${range.expected_soc_pct}%`,
        tone: range.range_risk ? 'text-t2' : undefined,
      },
      {
        label: 'short by',
        value: range.shortfall_km == null ? '—' : `${range.shortfall_km} km`,
        tone: range.shortfall_km != null ? 'text-t2' : undefined,
      },
      {
        label: 'margin',
        value: unit(range.margin_km, 'km', 1),
        tone: range.margin_km != null && range.margin_km < 0 ? 'text-t2' : undefined,
      },
      { label: 'next hub', value: progress?.next_hub ?? '—' },
    ]
    sections.push({
      id: 'range',
      title: 'Range & arrival SoC',
      subtitle: progress?.next_hub ? `Next · ${progress.next_hub}` : 'Corridor estimate',
      accent: range.range_risk ? 'warn' : 'neutral',
      highlights: rangeFields.slice(0, 4),
      fields: rangeFields,
    })

    const telemetryFields: DetailField[] = [
      { label: 'speed', value: unit(vehicle.speed_kph, 'KMPH') },
      { label: 'heading', value: num(vehicle.heading_deg, 0) },
      { label: 'SoC', value: vehicle.soc_pct == null ? '—' : `${vehicle.soc_pct}%` },
      { label: 'power', value: unit(vehicle.power_kw, 'kW') },
      { label: 'pack V', value: unit(vehicle.pack_voltage, 'V') },
      { label: 'pack A', value: unit(vehicle.pack_current, 'A') },
      { label: 'coolant', value: vehicle.coolant_c == null ? '—' : `${num(vehicle.coolant_c, 0)}°C` },
      { label: 'MCU temp', value: vehicle.mcu_temp_c == null ? '—' : `${num(vehicle.mcu_temp_c, 0)}°C` },
      { label: 'motor', value: vehicle.motor_rpm == null ? '—' : `${vehicle.motor_rpm} rpm` },
      { label: 'odometer', value: unit(vehicle.odo_km, 'km', 0) },
    ]
    sections.push({
      id: 'telemetry',
      title: 'Telemetry',
      subtitle: 'What the device reported',
      highlights: telemetryFields.slice(0, 4),
      fields: telemetryFields,
    })

    // CURRENT LEG, not the vehicle's own *_session counters. Those are monotonic for the
    // vehicle's whole life in the state store (reset only on an ENERGY_EPOCH bump, last
    // 2026-08-19) -- reading them directly here blended ~2 months of driving into a card
    // that claimed to be "this session". progress.leg_* comes from
    // rules.trips.live_energy's own start-of-leg baseline instead -- the same
    // counter-difference-and-guard the closed trip's kwh_per_km is built from.
    //
    // progress can be stale, for a different (closed) leg, or simply missing (range.ts's
    // own documented risk for this same field) -- trip_id + a freshness check on progress.ts
    // is what tells a genuinely open leg apart from a stale record left over after close.
    // `>= 0` rejects a future-dated ts (clock skew) outright rather than clamping it to
    // "fresh" the way ago()/vehicleState() do elsewhere on this page -- deliberately
    // stricter here: this card asserts a specific leg's identity (trip_id/origin_hub), not
    // just a staleness badge, so a skewed clock hiding the card is the safer failure than
    // attributing live numbers to the wrong leg.
    const legOpen = progress?.trip_id != null
      && progress.ts != null
      && now - progress.ts >= 0
      && now - progress.ts <= STALE_AFTER_MS
    const legKm = legOpen ? progress?.leg_km ?? null : null
    const legKwhPerKm = legOpen ? progress?.leg_kwh_per_km ?? null : null
    // MIN_LEG_KM/the plausibility band govern whether the RATIO (kWh/km) is trustworthy --
    // the same predicate range.ts's own legEfficiency uses to decide kwh_per_km_source:
    // 'leg'. Distance and energy are not re-gated on it: live_energy() already withholds
    // them below its own 50 m guard, and a known 11 km leg should not read as "—" just
    // because the efficiency figure built from it isn't trustworthy yet.
    const legEfficiencyTrusted = legKm != null && legKm >= MIN_LEG_KM
      && legKwhPerKm != null
      && legKwhPerKm >= MIN_PLAUSIBLE_KWH_PER_KM && legKwhPerKm <= MAX_PLAUSIBLE_KWH_PER_KM
    if (legOpen) {
      const legFields: DetailField[] = [
        { label: 'distance', value: unit(legKm, 'km', 2) },
        { label: 'energy', value: unit(progress?.leg_kwh ?? null, 'kWh', 2) },
        { label: 'kWh/km', value: num(legEfficiencyTrusted ? legKwhPerKm : null, 3) },
        { label: 'origin', value: progress?.origin_hub ?? '—' },
        {
          label: 'leg duration',
          // started_ts falsy (0, absent) means "no reading", same convention as
          // format.ts's own ago()/clock() -- not a leg that opened at the Unix epoch.
          value: duration(progress?.started_ts ? (now - progress.started_ts) / 1000 : null),
        },
      ]
      sections.push({
        id: 'session',
        title: 'Current leg',
        subtitle: `Since ${progress?.origin_hub ?? '—'}`,
        highlights: legFields.slice(0, 4),
        fields: legFields,
      })
    } else {
      sections.push({
        id: 'session',
        title: 'Current leg',
        subtitle: 'No leg in progress',
        highlights: [],
        fields: [],
        body: 'No open leg for this vehicle right now.',
      })
    }

    if (kpi) {
      const kpiFields: DetailField[] = [
        { label: 'avg speed', value: unit(kpi.avg_speed_kph, 'KMPH') },
        { label: 'max speed', value: unit(kpi.max_speed_kph, 'KMPH') },
        { label: 'moving', value: duration(kpi.moving_sec) },
        { label: 'idle', value: duration(kpi.idle_sec) },
        { label: 'utilisation', value: pct(kpi.utilization_pct) },
        { label: 'energy', value: unit(kpi.energy_kwh, 'kWh', 2) },
        { label: 'kWh/km', value: num(kpi.kwh_per_km, 3) },
        { label: 'SoC drop', value: kpi.soc_drop_pct == null ? '—' : `${kpi.soc_drop_pct}%` },
        {
          label: 'harsh brake',
          value: String(kpi.harsh_brakes),
          tone: kpi.harsh_brakes ? 'text-t1' : undefined,
        },
        {
          label: 'harsh accel',
          value: String(kpi.harsh_accels),
          tone: kpi.harsh_accels ? 'text-t1' : undefined,
        },
      ]
      sections.push({
        id: 'kpi',
        title: `Last closed minute · ${new Date(kpi.window_start).toTimeString().slice(0, 5)}`,
        subtitle: 'Windowed KPIs',
        highlights: kpiFields.slice(0, 4),
        fields: kpiFields,
      })
    } else {
      sections.push({
        id: 'kpi',
        title: 'Last closed minute',
        subtitle: 'No closed window yet',
        highlights: [{ label: 'status', value: 'waiting' }],
        fields: [],
        body: 'KPIs are suppressed until the window can no longer change — correct and about three minutes late, by design.',
      })
    }

    const trustFields: DetailField[] = [
      { label: 'OBD frames', value: String(vehicle.obd_frames) },
      { label: 'GPS fixes', value: String(vehicle.geo_fixes) },
      {
        label: 'sentinels',
        value: String(vehicle.sentinel_count),
        tone: vehicle.sentinel_count > 0 ? 'text-t1' : undefined,
      },
      { label: 'satellites', value: num(vehicle.satellites, 0) },
      { label: 'OBD lag', value: unit(vehicle.obd_lag_sec, 's', 0) },
      { label: 'GPS lag', value: unit(vehicle.geo_lag_sec, 's', 0) },
      ...(kpi ? [{
        label: 'coolant cov.',
        value: pct(kpi.coolant_coverage_pct),
        tone: kpi.coolant_coverage_pct < 50 ? 'text-t1' : undefined,
      } satisfies DetailField] : []),
      { label: 'device', value: vehicle.device_id ?? '—' },
    ]
    sections.push({
      id: 'trust',
      title: 'Trust',
      subtitle: 'Coverage, lag, sentinels',
      accent: vehicle.sentinel_count > 0 ? 'warn' : 'neutral',
      highlights: trustFields.slice(0, 4),
      fields: trustFields,
    })

    return {
      vehicle,
      meta,
      down,
      state,
      stateColor: STATE_COLOR[state],
      where,
      seenLabel: ago(seen, now),
      load,
      loadRoute,
      flag,
      sections,
      driverPhone: meta?.driver_phone ?? null,
      driverName,
      trip,
    }
  }, [vehicleId, vehicle, kpi, fenceIndex, down, meta, trip, progress, alerts, geofences, now])
}
